import type { $Fetch } from 'ofetch';
import type {
  GrupoVenda, UpsertGrupoVendaCommand,
  LinkCaptacao, UpsertLinkCaptacaoCommand,
  ProcessoSeletivo, UpsertProcessoSeletivoCommand,
  Inscricao, CriarInscricaoCommand,
} from '@/domain/model/ComercialGaps';
import type {
  GrupoVendaGateway, LinkCaptacaoGateway, ProcessoSeletivoGateway,
} from '@/application/port/out/ComercialGapsGateways';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/** Grupos de venda. Authorities `commerce:grupovenda:read|manage`. */
export class HttpGrupoVendaGateway implements GrupoVendaGateway {
  private readonly path = '/api/v1/commerce/grupos-venda';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<GrupoVenda[]> {
    const res = await this.client<ApiResponse<GrupoVenda[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertGrupoVendaCommand): Promise<GrupoVenda> {
    const res = await this.client<ApiResponse<GrupoVenda>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertGrupoVendaCommand): Promise<GrupoVenda> {
    const res = await this.client<ApiResponse<GrupoVenda>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Links de captação. Authorities `commerce:linkcaptacao:read|manage`. */
export class HttpLinkCaptacaoGateway implements LinkCaptacaoGateway {
  private readonly path = '/api/v1/commerce/links-captacao';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<LinkCaptacao[]> {
    const res = await this.client<ApiResponse<LinkCaptacao[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertLinkCaptacaoCommand): Promise<LinkCaptacao> {
    const res = await this.client<ApiResponse<LinkCaptacao>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertLinkCaptacaoCommand): Promise<LinkCaptacao> {
    const res = await this.client<ApiResponse<LinkCaptacao>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Processos seletivos + inscrições. Authorities `commerce:processoseletivo:read|manage`. */
export class HttpProcessoSeletivoGateway implements ProcessoSeletivoGateway {
  private readonly path = '/api/v1/commerce/processos-seletivos';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<ProcessoSeletivo[]> {
    const res = await this.client<ApiResponse<ProcessoSeletivo[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertProcessoSeletivoCommand): Promise<ProcessoSeletivo> {
    const res = await this.client<ApiResponse<ProcessoSeletivo>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertProcessoSeletivoCommand): Promise<ProcessoSeletivo> {
    const res = await this.client<ApiResponse<ProcessoSeletivo>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async abrir(id: string): Promise<ProcessoSeletivo> {
    const res = await this.client<ApiResponse<ProcessoSeletivo>>(`${this.path}/${encodeURIComponent(id)}/abrir`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
  async encerrar(id: string): Promise<ProcessoSeletivo> {
    const res = await this.client<ApiResponse<ProcessoSeletivo>>(`${this.path}/${encodeURIComponent(id)}/encerrar`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
  async listInscricoes(id: string): Promise<Inscricao[]> {
    const res = await this.client<ApiResponse<Inscricao[]>>(`${this.path}/${encodeURIComponent(id)}/inscricoes`, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async criarInscricao(id: string, cmd: CriarInscricaoCommand): Promise<Inscricao> {
    const res = await this.client<ApiResponse<Inscricao>>(`${this.path}/${encodeURIComponent(id)}/inscricoes`, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async aprovarInscricao(inscricaoId: string): Promise<Inscricao> {
    const res = await this.client<ApiResponse<Inscricao>>(`${this.path}/inscricoes/${encodeURIComponent(inscricaoId)}/aprovar`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
  async reprovarInscricao(inscricaoId: string): Promise<Inscricao> {
    const res = await this.client<ApiResponse<Inscricao>>(`${this.path}/inscricoes/${encodeURIComponent(inscricaoId)}/reprovar`, { method: 'POST' });
    assertApiSuccess(res); return res.data;
  }
}
